// src/components/calendar.jsx
// Monthly calendar showing planned events, tasks, AND HubSpot-logged activity.
//
// For non-admin users: shows their own data only.
// For admin users: defaults to "All Users" merged view with a dropdown filter
// to focus on a single rep.

import { useState, useEffect, useMemo } from "react";
import * as HS from "../hubspot.js";
import { CAL_TYPES } from "../constants.js";
import { sGet, sSet } from "../helpers.js";
import { Btn, FI, Spin, ActivitySummary } from "./ui.jsx";
import { findOwner } from "./owners.jsx";

// HubSpot activity time window: 1 month back, current, 1 month forward
const HS_LOOKBACK_DAYS  = 30;
const HS_LOOKAHEAD_DAYS = 30;
// Cache HubSpot activity for 5 minutes to avoid re-fetching on every render
const HS_CACHE_MS = 5 * 60 * 1000;

// Maps a HubSpot activity type to a calendar type for icon/color rendering
function mapActivityType(t) {
  if (t === "call")  return "call";
  if (t === "email") return "email";
  if (t === "note")  return "note";
  if (t === "visit") return "visit";
  return "other";
}

// Color palette for differentiating users when admin views "All"
const USER_COLORS = [
  "#b01020", "#1a7a3a", "#9b6ec8", "#e8a838", "#4a9e6e",
  "#c0614a", "#444444", "#6b8cae", "#a8553b", "#5d7e9f",
];
function colorForUser(userId) {
  if (!userId) return "#666666";
  // Stable hash so each user always gets the same color
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = ((h << 5) - h) + userId.charCodeAt(i);
    h |= 0;
  }
  return USER_COLORS[Math.abs(h) % USER_COLORS.length];
}

// In-memory cache shared across calendar mounts within the session
const hsCache = {
  key: null,      // serialized cache key (owners + range)
  data: null,     // array of activity items
  fetchedAt: 0,
};

export function CalendarView({currentUser, users, companies}) {
  const isAdmin = currentUser.isAdmin;
  const now = new Date();
  const [yr, setYr] = useState(now.getFullYear());
  const [mo, setMo] = useState(now.getMonth());

  // Admin user filter: "all" = everyone, "me" = current admin, "<userId>" = specific rep
  const [viewUser, setViewUser] = useState(isAdmin ? "all" : "me");

  // Local events + tasks for the user(s) being viewed
  const [events, setEvents] = useState([]);
  const [tasks,  setTasks]  = useState([]);

  // HubSpot-logged activity (calls/notes/emails) for the user(s) being viewed
  const [hsActivity, setHsActivity] = useState([]);
  const [hsLoading,  setHsLoading]  = useState(false);

  const [dayDetail, setDayDetail] = useState(null);
  const [showAdd,   setShowAdd]   = useState(false);
  const [newEv, setNewEv] = useState({title:"", type:"call", companyId:"", note:"", time:""});

  // Activity type filter: Set of type keys that are HIDDEN. Empty set = show all.
  // Persisted across reloads since it's a per-user preference.
  const [hiddenTypes, setHiddenTypes] = useState(() => {
    try {
      const raw = localStorage.getItem("crm_cal_hidden_types");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  const persistHiddenTypes = (next) => {
    setHiddenTypes(next);
    try { localStorage.setItem("crm_cal_hidden_types", JSON.stringify([...next])); } catch {}
  };
  const toggleType = (k) => {
    const next = new Set(hiddenTypes);
    if (next.has(k)) next.delete(k); else next.add(k);
    persistHiddenTypes(next);
  };
  const showAllTypes  = () => persistHiddenTypes(new Set());
  const hideAllTypes  = () => persistHiddenTypes(new Set(Object.keys(CAL_TYPES)));

  // Resolve which CRM users we're viewing
  const viewedUsers = useMemo(() => {
    if (!isAdmin) return [currentUser];
    if (viewUser === "all") return (users || []).filter(u => !u.isAdmin);
    if (viewUser === "me")  return [currentUser];
    const u = (users || []).find(u => u.id === viewUser);
    return u ? [u] : [];
  }, [isAdmin, viewUser, users, currentUser]);

  // Resolve HubSpot owner IDs for the users we're viewing
  const viewedOwnerIds = useMemo(() => {
    return viewedUsers
      .map(u => u.hubspotOwnerId || (String(u.id || "").startsWith("hs_") ? String(u.id).slice(3) : null))
      .filter(Boolean);
  }, [viewedUsers]);

  // ── Load local events + tasks for the viewed user(s) ──────────────────────
  useEffect(() => {
    const allEvents = [];
    const allTasks  = [];
    for (const u of viewedUsers) {
      const evs = sGet("crm_cal_"   + u.id) || [];
      const tks = sGet("crm_tasks_" + u.id) || [];
      // Tag with userId so we can color/badge them in the merged view
      evs.forEach(e => allEvents.push({...e, _userId: u.id}));
      tks.forEach(t => allTasks.push({...t,  _userId: u.id}));
    }
    setEvents(allEvents);
    setTasks(allTasks);
  }, [viewedUsers]);

  // ── Load HubSpot activity for the viewed user(s) ──────────────────────────
  useEffect(() => {
    if (viewedOwnerIds.length === 0) {
      setHsActivity([]);
      return;
    }
    const cacheKey = viewedOwnerIds.slice().sort().join(",");
    const fresh = hsCache.key === cacheKey && (Date.now() - hsCache.fetchedAt) < HS_CACHE_MS;
    if (fresh) {
      setHsActivity(hsCache.data || []);
      return;
    }
    let cancelled = false;
    (async () => {
      setHsLoading(true);
      const start = Date.now() - HS_LOOKBACK_DAYS  * 24 * 60 * 60 * 1000;
      const end   = Date.now() + HS_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000;
      try {
        const acts = await HS.getActivitiesByOwners(viewedOwnerIds, start, end);
        if (cancelled) return;
        hsCache.key       = cacheKey;
        hsCache.data      = acts;
        hsCache.fetchedAt = Date.now();
        setHsActivity(acts);
      } catch (e) {
        console.warn("Calendar HubSpot activity load failed:", e.message);
        if (!cancelled) setHsActivity([]);
      }
      if (!cancelled) setHsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [viewedOwnerIds.join(",")]);

  // Save events back to localStorage (only when it's the current user's events)
  const saveEvs = next => {
    setEvents(next);
    // Only persist events that belong to the current user; we shouldn't
    // overwrite other users' calendars when admin is in merged view.
    const mine = next.filter(e => !e._userId || e._userId === currentUser.id);
    sSet("crm_cal_" + currentUser.id, mine.map(e => { const {_userId, ...rest} = e; return rest; }));
  };

  // ── Convert tasks to calendar events ──────────────────────────────────────
  const taskEvs = tasks.filter(t => t.dueDate).map(t => ({
    id: "tk_" + t.id + "_" + (t._userId || ""),
    date: t.dueDate,
    title: t.title,
    type: t.type === "visit" ? "visit"
        : t.type === "email" ? "email"
        : t.type === "quote" ? "quote"
        : "call",
    companyId: t.companyId || "",
    note: t.note || "",
    time: "",
    fromTask: true,
    done: t.done,
    _userId: t._userId,
  }));

  // ── Convert HubSpot activity to calendar events ───────────────────────────
  const hsEvs = hsActivity.map(a => {
    // Find which CRM user this activity belongs to (via hubspotOwnerId match)
    const owningUser = (users || []).find(u =>
      u.hubspotOwnerId && String(u.hubspotOwnerId) === String(a.ownerId)
    );
    return {
      id: "hs_" + a.hsId,
      date: a.date,
      title: a.summary || (a.type.charAt(0).toUpperCase() + a.type.slice(1)),
      type: mapActivityType(a.type),
      companyId: "",
      note: "",
      time: a.time || "",
      fromHubSpot: true,
      _userId: owningUser?.id || a.ownerId,
      // For ActivitySummary - the canonical activity id and raw content
      _activityId: a.id,
      _content:    a.content || a.summary || "",
    };
  });

  // Unfiltered list -- used to compute total counts per type so chips show
  // accurate totals regardless of which types are currently hidden.
  const allEvsUnfiltered = [...events, ...taskEvs, ...hsEvs];
  // Filtered by hidden types -- used everywhere else (calendar grid, day detail,
  // activity log table). Hidden types are completely invisible.
  const allEvs = allEvsUnfiltered.filter(e => !hiddenTypes.has(e.type));

  const dim = (y, m) => new Date(y, m+1, 0).getDate();
  const fdow = (y, m) => new Date(y, m, 1).getDay();
  const key = (y, m, d) => y + "-" + String(m+1).padStart(2,"0") + "-" + String(d).padStart(2,"0");
  const todayKey = key(now.getFullYear(), now.getMonth(), now.getDate());
  const mName = new Date(yr, mo, 1).toLocaleString("en-US",{month:"long"});

  const prevMo = () => { if (mo === 0) {setMo(11); setYr(y => y-1);} else setMo(m => m-1); };
  const nextMo = () => { if (mo === 11) {setMo(0); setYr(y => y+1);} else setMo(m => m+1); };

  const onDay = dk => allEvs.filter(e => e.date === dk);
  const mKey = yr + "-" + String(mo+1).padStart(2,"0");
  const mEvs = allEvs.filter(e => e.date && e.date.startsWith(mKey));
  // Type counts are computed from the UNFILTERED list so chips display the
  // total available for each type, not "hidden = 0". Visually we'll dim
  // chips that are toggled off.
  const mEvsAll = allEvsUnfiltered.filter(e => e.date && e.date.startsWith(mKey));
  const typeCt = Object.keys(CAL_TYPES).reduce((a, k) => ({...a, [k]: mEvsAll.filter(e => e.type === k).length}), {});
  const isOverDay = dk => dk < todayKey && taskEvs.some(e => e.date === dk && !e.done);

  const addEvent = () => {
    if (!newEv.title || !dayDetail) return;
    const ev = {...newEv, id: "ev_" + Date.now(), date: dayDetail, _userId: currentUser.id};
    saveEvs([...events, ev]);
    setNewEv({title:"", type:"call", companyId:"", note:"", time:""});
    setShowAdd(false);
  };
  const delEvent = id => {
    const target = events.find(e => e.id === id);
    if (!target) return;
    if (target._userId && target._userId !== currentUser.id) {
      // Don't allow deleting another user's event from the merged view
      return;
    }
    saveEvs(events.filter(e => e.id !== id));
  };

  const cells = [];
  for (let i = 0; i < fdow(yr, mo); i++) cells.push(null);
  for (let d = 1; d <= dim(yr, mo); d++) cells.push(d);
  // Pad trailing cells to complete the final week so the grid is rectangular
  while (cells.length % 7 !== 0) cells.push(null);
  const detEvs = dayDetail ? onDay(dayDetail) : [];

  // Helper for displaying who an event belongs to (only relevant in merged view)
  const showsMultipleUsers = isAdmin && viewUser === "all" && viewedUsers.length > 1;
  const userLabel = (uid) => {
    const u = findOwner(uid, users || []);
    if (!u) return null;
    return u.name || (u.email ? u.email.split("@")[0] : "User");
  };

  return (
    <div style={{animation:"fadeUp .35s ease both"}}>
      {/* Top controls row */}
      <div style={{display:"flex",gap:12,marginBottom:18,alignItems:"flex-start",flexWrap:"wrap"}}>
        <div style={{background:"#fff",borderRadius:14,padding:"12px 18px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",display:"flex",alignItems:"center",gap:14,flexShrink:0}}>
          <button onClick={prevMo} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--brown)"}}>‹</button>
          <div style={{textAlign:"center",minWidth:120}}>
            <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:18,fontWeight:600,color:"var(--dark)"}}>{mName}</div>
            <div style={{fontSize:11,color:"var(--muted)"}}>{yr}</div>
          </div>
          <button onClick={nextMo} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--brown)"}}>›</button>
          <button onClick={()=>{setYr(now.getFullYear()); setMo(now.getMonth());}}
            style={{padding:"4px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:"var(--warm1)",fontSize:11,color:"var(--brown)",fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>Today</button>
        </div>

        {/* Admin: user picker */}
        {isAdmin && (users || []).filter(u => !u.isAdmin).length > 0 && (
          <div style={{background:"#fff",borderRadius:14,padding:"10px 14px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Viewing</span>
            <select value={viewUser} onChange={e=>setViewUser(e.target.value)}
              style={{padding:"6px 12px",borderRadius:8,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,fontFamily:"Barlow,sans-serif",fontWeight:600,color:"var(--brown)",cursor:"pointer",outline:"none"}}>
              <option value="all">👥 All Users ({(users||[]).filter(u=>!u.isAdmin).length})</option>
              <option value="me">👤 My Calendar (Admin)</option>
              <optgroup label="Sales Reps">
                {(users || []).filter(u => !u.isAdmin).map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </optgroup>
            </select>
          </div>
        )}

        <div style={{flex:1,minWidth:200,background:"#fff",borderRadius:14,padding:"12px 16px",boxShadow:"var(--sh)",border:"1px solid var(--warm2)"}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5,marginBottom:8,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            <span>{mName} - {mEvs.length} of {mEvsAll.length} shown</span>
            {hsLoading && <span style={{fontWeight:400,fontStyle:"italic",textTransform:"none",letterSpacing:0}}><Spin/> loading HubSpot activity...</span>}
            {hiddenTypes.size > 0 && (
              <button onClick={showAllTypes}
                style={{marginLeft:"auto",padding:"3px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:"var(--warm1)",fontSize:10,color:"var(--brown)",fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif",textTransform:"none",letterSpacing:0}}
                title="Show all activity types">
                ✕ Clear filter ({hiddenTypes.size} hidden)
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {Object.entries(CAL_TYPES).filter(([k]) => typeCt[k] > 0).map(([k, cfg]) => {
              const isHidden = hiddenTypes.has(k);
              return (
                <button key={k} onClick={()=>toggleType(k)}
                  title={isHidden ? "Show " + cfg.label + "s" : "Hide " + cfg.label + "s"}
                  style={{
                    display:"flex",alignItems:"center",gap:4,
                    padding:"3px 9px",borderRadius:99,
                    background: isHidden ? "#fff" : cfg.bg,
                    border:"1px solid " + (isHidden ? "var(--warm2)" : cfg.color + "33"),
                    fontSize:12,cursor:"pointer",fontFamily:"Barlow,sans-serif",
                    opacity: isHidden ? 0.5 : 1,
                    textDecoration: isHidden ? "line-through" : "none",
                    transition:"all .12s",
                  }}>
                  <span>{cfg.icon}</span>
                  <span style={{fontWeight:600,color:isHidden?"var(--muted)":cfg.color}}>{typeCt[k]}</span>
                  <span style={{color:"var(--muted)"}}>{cfg.label}</span>
                </button>
              );
            })}
            {mEvsAll.length === 0 && !hsLoading && <span style={{fontSize:12,color:"var(--muted)",fontStyle:"italic"}}>No events this month</span>}
          </div>
        </div>
      </div>

      {/* User color legend (admin merged view only) */}
      {showsMultipleUsers && (
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:14,padding:"8px 14px",background:"#fff",borderRadius:10,border:"1px solid var(--warm2)"}}>
          <span style={{fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>Users:</span>
          {viewedUsers.map(u => (
            <span key={u.id} style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:12}}>
              <span style={{width:12,height:12,borderRadius:99,background:colorForUser(u.id),flexShrink:0}}/>
              <span style={{color:"var(--text)",fontWeight:500}}>{u.name || u.email.split("@")[0]}</span>
            </span>
          ))}
        </div>
      )}

      {/* Calendar + day detail */}
      <div className="g2" style={{display:"grid",gridTemplateColumns:"1fr 280px",gap:14,alignItems:"start"}}>
        <div style={{background:"#fff",borderRadius:14,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",overflow:"hidden"}}>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",borderBottom:"1px solid var(--warm2)"}}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
              <div key={d} style={{padding:"8px 0",textAlign:"center",fontSize:11,fontWeight:600,color:"var(--muted)",textTransform:"uppercase"}}>{d}</div>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)"}}>
            {cells.map((day, i) => {
              if (!day) return <div key={"e"+i} style={{height:90,borderRight:"1px solid var(--warm2)",borderBottom:"1px solid var(--warm2)",background:"#fafafa"}}/>;
              const dk = key(yr, mo, day);
              const evs = onDay(dk);
              const isToday = dk === todayKey;
              const isSel = dayDetail === dk;
              const over = isOverDay(dk);
              const isPast = dk < todayKey;
              return (
                <div key={dk} onClick={()=>setDayDetail(isSel ? null : dk)}
                  style={{
                    height:90,
                    borderRight:"1px solid var(--warm2)",
                    borderBottom:"1px solid var(--warm2)",
                    padding:5,cursor:"pointer",transition:"background .12s",
                    background:isSel?"var(--warm1)":over?"#fff5f4":isPast&&evs.length===0?"#fafafa":"#fff",
                    outline:isToday?"2px solid var(--brown)":"none",
                    outlineOffset:-2,
                    overflow:"hidden",
                    display:"flex",
                    flexDirection:"column",
                  }}
                  onMouseEnter={e=>{if(!isSel) e.currentTarget.style.background="var(--warm1)";}}
                  onMouseLeave={e=>{if(!isSel) e.currentTarget.style.background=over?"#fff5f4":isPast&&evs.length===0?"#fafafa":"#fff";}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:2}}>
                    <span style={{
                      fontSize:12,fontWeight:isToday?700:500,
                      color:isToday?"var(--brown)":isPast?"var(--muted)":"var(--dark)",
                      width:20,height:20,borderRadius:99,
                      background:isToday?"var(--warm2)":"transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",
                    }}>{day}</span>
                    {over && <span style={{fontSize:9,color:"var(--red)",fontWeight:700}}>!</span>}
                  </div>
                  {evs.slice(0, 2).map(ev => {
                    const cfg = CAL_TYPES[ev.type] || CAL_TYPES.other;
                    const showUserDot = showsMultipleUsers && ev._userId;
                    return (
                      <div key={ev.id} style={{
                        display:"flex",alignItems:"center",gap:3,
                        fontSize:9,padding:"1px 4px",borderRadius:3,
                        background:cfg.bg,color:cfg.color,fontWeight:600,
                        marginBottom:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                        opacity:ev.done?.5:1,
                        borderLeft: showUserDot ? "3px solid " + colorForUser(ev._userId) : "none",
                        flexShrink:0,
                        maxWidth:"100%",
                      }}>
                        <span style={{flexShrink:0}}>{cfg.icon}</span>
                        <span style={{overflow:"hidden",textOverflow:"ellipsis",minWidth:0}}>{ev.title}</span>
                      </div>
                    );
                  })}
                  {evs.length > 2 && (
                    <div style={{fontSize:9,color:"var(--brown)",fontWeight:600,paddingLeft:2,marginTop:1}}>
                      +{evs.length - 2} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Day detail panel */}
        <div style={{background:"#fff",borderRadius:14,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",overflow:"hidden",position:"sticky",top:16}}>
          {dayDetail ? (
            <>
              <div style={{padding:"14px 16px",borderBottom:"1px solid var(--warm1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:15,fontWeight:600,color:"var(--dark)"}}>
                    {new Date(dayDetail + "T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                  </div>
                  <div style={{fontSize:11,color:"var(--muted)"}}>{detEvs.length} event{detEvs.length !== 1 ? "s" : ""}</div>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>setShowAdd(true)} style={{padding:"4px 10px",borderRadius:99,border:"1px solid var(--warm2)",background:"var(--warm1)",fontSize:11,color:"var(--brown)",fontWeight:600,cursor:"pointer",fontFamily:"Barlow,sans-serif"}}>+ Add</button>
                  <button onClick={()=>{setDayDetail(null); setShowAdd(false);}} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:"var(--muted)",lineHeight:1}}>×</button>
                </div>
              </div>
              {showAdd && (
                <div style={{padding:"12px 16px",borderBottom:"1px solid var(--warm1)",background:"var(--warm1)",display:"flex",flexDirection:"column",gap:8}}>
                  <FI label="Title" value={newEv.title} onChange={v => setNewEv(e => ({...e, title: v}))} placeholder="e.g. Call with Kingsdown"/>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <label style={{fontSize:10,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.4}}>Type</label>
                      <select value={newEv.type} onChange={e=>setNewEv(ev=>({...ev, type: e.target.value}))} style={{padding:"7px 10px",borderRadius:8,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:12,outline:"none"}}>
                        {Object.entries(CAL_TYPES).map(([k, c]) => <option key={k} value={k}>{c.icon} {c.label}</option>)}
                      </select>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:4}}>
                      <label style={{fontSize:10,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.4}}>Time</label>
                      <input type="time" value={newEv.time} onChange={e=>setNewEv(ev=>({...ev, time: e.target.value}))} style={{padding:"7px 10px",borderRadius:8,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:12,outline:"none"}}/>
                    </div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    <label style={{fontSize:10,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.4}}>Company</label>
                    <select value={newEv.companyId} onChange={e=>setNewEv(ev=>({...ev, companyId: e.target.value}))} style={{padding:"7px 10px",borderRadius:8,border:"1.5px solid var(--warm2)",background:"#fff",fontSize:12,outline:"none"}}>
                      <option value="">- None -</option>
                      {companies.map(c => <option key={c.id} value={c.id}>{c.properties.name}</option>)}
                    </select>
                  </div>
                  <FI label="Notes" value={newEv.note} onChange={v=>setNewEv(e=>({...e, note: v}))} placeholder="Optional..." multiline rows={2}/>
                  <div style={{display:"flex",gap:6}}>
                    <Btn onClick={addEvent} disabled={!newEv.title} style={{fontSize:11,padding:"6px 12px"}}>Save</Btn>
                    <Btn variant="ghost" onClick={()=>{setShowAdd(false); setNewEv({title:"",type:"call",companyId:"",note:"",time:""});}} style={{fontSize:11,padding:"6px 12px"}}>Cancel</Btn>
                  </div>
                </div>
              )}
              <div style={{padding:"10px 14px",maxHeight:400,overflowY:"auto"}}>
                {detEvs.length === 0 && <div style={{padding:"20px 0",textAlign:"center",color:"var(--muted)",fontSize:12,fontStyle:"italic"}}>No events - click + Add.</div>}
                {[...detEvs].sort((a,b) => (a.time||"").localeCompare(b.time||"")).map(ev => {
                  const cfg = CAL_TYPES[ev.type] || CAL_TYPES.other;
                  const co = companies.find(c => c.id === ev.companyId);
                  const evUserName = ev._userId ? userLabel(ev._userId) : null;
                  const evUserColor = ev._userId ? colorForUser(ev._userId) : null;
                  return (
                    <div key={ev.id} style={{display:"flex",gap:8,padding:"8px 0",borderBottom:"1px solid var(--warm1)",opacity:ev.done?.55:1,borderLeft: showsMultipleUsers && evUserColor ? "3px solid " + evUserColor : "none", paddingLeft: showsMultipleUsers && evUserColor ? 8 : 0}}>
                      <div style={{width:28,height:28,borderRadius:99,background:cfg.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0}}>{cfg.icon}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <span style={{fontWeight:600,fontSize:12,color:"var(--dark)"}}>{ev.title}</span>
                          {!ev.fromTask && !ev.fromHubSpot && (!ev._userId || ev._userId === currentUser.id) && (
                            <button onClick={()=>delEvent(ev.id)} style={{background:"none",border:"none",cursor:"pointer",color:"var(--muted)",fontSize:13,padding:"0 2px",opacity:.5,flexShrink:0}} onMouseEnter={e=>e.currentTarget.style.opacity="1"} onMouseLeave={e=>e.currentTarget.style.opacity=".5"}>✕</button>
                          )}
                        </div>
                        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginTop:2}}>
                          <span style={{fontSize:10,padding:"1px 6px",borderRadius:99,background:cfg.bg,color:cfg.color,fontWeight:600}}>{cfg.label}</span>
                          {ev.time && <span style={{fontSize:10,color:"var(--muted)"}}>🕐 {ev.time}</span>}
                          {co && <span style={{fontSize:10,color:"var(--muted)"}}>🏢 {co.properties.name}</span>}
                          {ev.fromTask && <span style={{fontSize:10,color:"var(--muted)",fontStyle:"italic"}}>{ev.done ? "✓ Done" : "Task"}</span>}
                          {ev.fromHubSpot && <span style={{fontSize:10,color:"var(--muted)",fontStyle:"italic"}}>📡 HubSpot</span>}
                          {showsMultipleUsers && evUserName && <span style={{fontSize:10,color:evUserColor,fontWeight:600}}>{evUserName}</span>}
                        </div>
                        {ev.note && <div style={{fontSize:10,color:"var(--muted)",marginTop:2,fontStyle:"italic"}}>{ev.note}</div>}
                        {ev.fromHubSpot && ev._activityId && (
                          <div style={{fontSize:11,color:"var(--muted)",marginTop:4,lineHeight:1.5}}>
                            <ActivitySummary activity={{id: ev._activityId, content: ev._content, summary: ev.title}} fallback={ev.title}/>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div style={{padding:"40px 20px",textAlign:"center",color:"var(--muted)"}}>
              <div style={{fontSize:32,marginBottom:10}}>📅</div>
              <div style={{fontSize:13,fontWeight:600,color:"var(--dark)",marginBottom:6}}>Select a Day</div>
              <div style={{fontSize:12,lineHeight:1.6}}>Click any day to view events and add new entries.</div>
            </div>
          )}
        </div>
      </div>

      {/* Activity log table */}
      {mEvs.length > 0 && (
        <div style={{marginTop:20,background:"#fff",borderRadius:14,boxShadow:"var(--sh)",border:"1px solid var(--warm2)",overflow:"hidden"}}>
          <div style={{padding:"12px 18px",borderBottom:"1px solid var(--warm1)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontFamily:"Barlow Condensed,sans-serif",fontSize:15,fontWeight:600,color:"var(--dark)"}}>{mName} Activity Log</div>
            <span style={{fontSize:12,color:"var(--muted)"}}>{mEvs.length} entries</span>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{borderBottom:"1px solid var(--warm2)",background:"var(--warm1)"}}>
                  {["Date","Time","Type","Title","Source", showsMultipleUsers ? "User" : null,"Company","Notes"].filter(Boolean).map(h => (
                    <th key={h} style={{textAlign:"left",padding:"9px 14px",fontWeight:600,color:"var(--muted)",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...mEvs].sort((a,b) => a.date.localeCompare(b.date) || (a.time||"").localeCompare(b.time||"")).map((ev, i) => {
                  const cfg = CAL_TYPES[ev.type] || CAL_TYPES.other;
                  const co = companies.find(c => c.id === ev.companyId);
                  const evUserName = ev._userId ? userLabel(ev._userId) : null;
                  return (
                    <tr key={ev.id} style={{borderBottom:"1px solid var(--warm1)",background:i%2===0?"#fff":"#fdfcfa"}}>
                      <td style={{padding:"9px 14px",fontWeight:500,whiteSpace:"nowrap"}}>{new Date(ev.date + "T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}</td>
                      <td style={{padding:"9px 14px",color:"var(--muted)"}}>{ev.time || "-"}</td>
                      <td style={{padding:"9px 14px"}}><span style={{fontSize:11,padding:"2px 8px",borderRadius:99,background:cfg.bg,color:cfg.color,fontWeight:600}}>{cfg.icon} {cfg.label}</span></td>
                      <td style={{padding:"9px 14px",fontWeight:500}}>{ev.title}</td>
                      <td style={{padding:"9px 14px",color:"var(--muted)",fontSize:11}}>{ev.fromHubSpot ? "📡 HubSpot" : ev.fromTask ? "✓ Task" : "📅 Manual"}</td>
                      {showsMultipleUsers && <td style={{padding:"9px 14px",color:ev._userId?colorForUser(ev._userId):"var(--muted)",fontWeight:600,fontSize:11}}>{evUserName || "-"}</td>}
                      <td style={{padding:"9px 14px",color:"var(--muted)"}}>{co?.properties.name || "-"}</td>
                      <td style={{padding:"9px 14px",color:"var(--muted)",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ev.note || "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
